/**
 * Comprehensive SLP (Student Learning Portfolio) Management Page
 * Provides complete SLP management with data-rich interface
 */

import React, { useState, useEffect } from 'react';
import SLPService from '../../lib/services/slpService';
import './SLPManagementPage.css';

const SLPManagementPage = ({ onBack, onTabChange, context = {} }) => {
  const [slpData, setSlpData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'activities', 'achievements', 'statistics'
  const [selectedStudent, setSelectedStudent] = useState(context.studentId || null);
  const [selectedClass, setSelectedClass] = useState(context.classId || null);
  const [slpService] = useState(new SLPService('tn_01'));

  useEffect(() => {
    fetchSLPData();
  }, [selectedStudent, selectedClass]);

  const fetchSLPData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching SLP data...', { selectedStudent, selectedClass });
      
      const { data, error: fetchError } = await slpService.getSLPData(selectedStudent, selectedClass);
      
      if (fetchError) {
        console.error('Error fetching SLP data:', fetchError);
        setError(fetchError);
        return;
      }
      
      if (!data) {
        console.error('No SLP data returned');
        setError('No SLP data available');
        return;
      }
      
      setSlpData(data);
      console.log('SLP data loaded successfully:', data);
      
    } catch (err) {
      console.error('Unexpected error in fetchSLPData:', err);
      setError(err.message || 'Failed to fetch SLP data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateActivity = () => {
    // This would open a create activity modal
    console.log('Create activity clicked');
  };

  const handleCreateAchievement = () => {
    // This would open a create achievement modal
    console.log('Create achievement clicked');
  };

  const handleEditActivity = (activity) => {
    // This would open an edit activity modal
    console.log('Edit activity clicked:', activity);
  };

  const handleEditAchievement = (achievement) => {
    // This would open an edit achievement modal
    console.log('Edit achievement clicked:', achievement);
  };

  const handleDeleteActivity = async (activityId) => {
    if (window.confirm('Are you sure you want to delete this activity?')) {
      try {
        const { error } = await slpService.deleteActivity(activityId);
        if (error) {
          console.error('Error deleting activity:', error);
          return;
        }
        await fetchSLPData(); // Refresh data
      } catch (err) {
        console.error('Error deleting activity:', err);
      }
    }
  };

  const handleDeleteAchievement = async (achievementId) => {
    if (window.confirm('Are you sure you want to delete this achievement?')) {
      try {
        const { error } = await slpService.deleteAchievement(achievementId);
        if (error) {
          console.error('Error deleting achievement:', error);
          return;
        }
        await fetchSLPData(); // Refresh data
      } catch (err) {
        console.error('Error deleting achievement:', err);
      }
    }
  };

  const getActivityTypeColor = (type) => {
    const colors = {
      'academic': '#3b82f6',
      'sports': '#10b981',
      'arts': '#f59e0b',
      'volunteer': '#8b5cf6',
      'leadership': '#ef4444',
      'other': '#6b7280'
    };
    return colors[type] || '#6b7280';
  };

  const getAchievementLevelColor = (level) => {
    const colors = {
      'gold': '#f59e0b',
      'silver': '#6b7280',
      'bronze': '#cd7f32',
      'participation': '#3b82f6',
      'other': '#6b7280'
    };
    return colors[level] || '#6b7280';
  };

  if (loading) {
    return (
      <div className="slp-management-page">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>← 返回主頁</button>
          <h1>學習歷程</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>載入學習歷程資料中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="slp-management-page">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>← 返回主頁</button>
          <h1>學習歷程</h1>
        </div>
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>載入錯誤</h3>
          <p>{error}</p>
          <button className="retry-button" onClick={fetchSLPData}>重試</button>
        </div>
      </div>
    );
  }

  return (
    <div className="slp-management-page">
      {/* Header */}
      <div className="page-header">
        <button className="back-button" onClick={onBack}>← 返回主頁</button>
        <h1>學習歷程</h1>
        <button className="refresh-button" onClick={fetchSLPData} title="重新載入">
          🔄
        </button>
      </div>

      {/* Navigation Tabs */}
      <div className="slp-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          概覽
        </button>
        <button
          className={`tab-btn ${activeTab === 'activities' ? 'active' : ''}`}
          onClick={() => setActiveTab('activities')}
        >
          活動記錄
        </button>
        <button
          className={`tab-btn ${activeTab === 'achievements' ? 'active' : ''}`}
          onClick={() => setActiveTab('achievements')}
        >
          成就記錄
        </button>
        <button
          className={`tab-btn ${activeTab === 'statistics' ? 'active' : ''}`}
          onClick={() => setActiveTab('statistics')}
        >
          統計分析
        </button>
      </div>

      {/* Content */}
      <div className="page-content">
        {activeTab === 'overview' && (
          <div className="overview-section">
            {/* Summary Cards */}
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-icon">🎯</div>
                <div className="card-content">
                  <h3>總活動數</h3>
                  <p className="card-value">{slpData.totalActivities}</p>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-icon">🏆</div>
                <div className="card-content">
                  <h3>總成就數</h3>
                  <p className="card-value">{slpData.totalAchievements}</p>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-icon">👥</div>
                <div className="card-content">
                  <h3>參與學生</h3>
                  <p className="card-value">{slpData.uniqueStudents}</p>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-icon">📚</div>
                <div className="card-content">
                  <h3>涉及班別</h3>
                  <p className="card-value">{slpData.uniqueClasses}</p>
                </div>
              </div>
            </div>

            {/* Recent Activities */}
            <div className="recent-activities">
              <h2>最近活動</h2>
              <div className="activities-grid">
                {slpData.recentActivities.map((activity, index) => (
                  <div key={index} className="activity-card">
                    <div className="activity-header">
                      <h4>{activity.title}</h4>
                      <span 
                        className="activity-type-badge"
                        style={{ backgroundColor: getActivityTypeColor(activity.activity_type) }}
                      >
                        {activity.activityType}
                      </span>
                    </div>
                    <div className="activity-details">
                      <p><strong>學生:</strong> {activity.studentName}</p>
                      <p><strong>班別:</strong> {activity.className}</p>
                      <p><strong>日期:</strong> {new Date(activity.activity_date).toLocaleDateString()}</p>
                      <p><strong>類別:</strong> {activity.category}</p>
                    </div>
                    <div className="activity-description">
                      <p>{activity.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Achievements */}
            <div className="recent-achievements">
              <h2>最近成就</h2>
              <div className="achievements-grid">
                {slpData.recentAchievements.map((achievement, index) => (
                  <div key={index} className="achievement-card">
                    <div className="achievement-header">
                      <h4>{achievement.title}</h4>
                      <span 
                        className="achievement-level-badge"
                        style={{ backgroundColor: getAchievementLevelColor(achievement.level) }}
                      >
                        {achievement.achievementLevel}
                      </span>
                    </div>
                    <div className="achievement-details">
                      <p><strong>學生:</strong> {achievement.studentName}</p>
                      <p><strong>班別:</strong> {achievement.className}</p>
                      <p><strong>日期:</strong> {new Date(achievement.achievement_date).toLocaleDateString()}</p>
                      <p><strong>類別:</strong> {achievement.category}</p>
                    </div>
                    <div className="achievement-description">
                      <p>{achievement.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="quick-actions">
              <h2>快速操作</h2>
              <div className="action-buttons">
                <button className="action-btn primary" onClick={handleCreateActivity}>
                  ➕ 新增活動
                </button>
                <button className="action-btn primary" onClick={handleCreateAchievement}>
                  🏆 新增成就
                </button>
                <button className="action-btn" onClick={() => setActiveTab('activities')}>
                  📋 查看所有活動
                </button>
                <button className="action-btn" onClick={() => setActiveTab('statistics')}>
                  📊 查看統計
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'activities' && (
          <div className="activities-section">
            <div className="section-header">
              <h2>活動記錄</h2>
              <button className="action-btn primary" onClick={handleCreateActivity}>
                ➕ 新增活動
              </button>
            </div>
            
            <div className="activities-table">
              <table>
                <thead>
                  <tr>
                    <th>活動名稱</th>
                    <th>學生姓名</th>
                    <th>班別</th>
                    <th>類型</th>
                    <th>類別</th>
                    <th>日期</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {slpData.activities.map((activity, index) => (
                    <tr key={index}>
                      <td>
                        <div className="activity-title">
                          <strong>{activity.title}</strong>
                          <p className="activity-description">{activity.description}</p>
                        </div>
                      </td>
                      <td>
                        <div className="student-info">
                          <div className="student-avatar">
                            {activity.studentName?.charAt(0) || '?'}
                          </div>
                          <span>{activity.studentName}</span>
                        </div>
                      </td>
                      <td>{activity.className}</td>
                      <td>
                        <span 
                          className="activity-type-badge"
                          style={{ backgroundColor: getActivityTypeColor(activity.activity_type) }}
                        >
                          {activity.activityType}
                        </span>
                      </td>
                      <td>{activity.category}</td>
                      <td>{new Date(activity.activity_date).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="action-btn small"
                            onClick={() => handleEditActivity(activity)}
                          >
                            編輯
                          </button>
                          <button 
                            className="action-btn small danger"
                            onClick={() => handleDeleteActivity(activity.id)}
                          >
                            刪除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'achievements' && (
          <div className="achievements-section">
            <div className="section-header">
              <h2>成就記錄</h2>
              <button className="action-btn primary" onClick={handleCreateAchievement}>
                🏆 新增成就
              </button>
            </div>
            
            <div className="achievements-table">
              <table>
                <thead>
                  <tr>
                    <th>成就名稱</th>
                    <th>學生姓名</th>
                    <th>班別</th>
                    <th>等級</th>
                    <th>類別</th>
                    <th>日期</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {slpData.achievements.map((achievement, index) => (
                    <tr key={index}>
                      <td>
                        <div className="achievement-title">
                          <strong>{achievement.title}</strong>
                          <p className="achievement-description">{achievement.description}</p>
                        </div>
                      </td>
                      <td>
                        <div className="student-info">
                          <div className="student-avatar">
                            {achievement.studentName?.charAt(0) || '?'}
                          </div>
                          <span>{achievement.studentName}</span>
                        </div>
                      </td>
                      <td>{achievement.className}</td>
                      <td>
                        <span 
                          className="achievement-level-badge"
                          style={{ backgroundColor: getAchievementLevelColor(achievement.level) }}
                        >
                          {achievement.achievementLevel}
                        </span>
                      </td>
                      <td>{achievement.category}</td>
                      <td>{new Date(achievement.achievement_date).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="action-btn small"
                            onClick={() => handleEditAchievement(achievement)}
                          >
                            編輯
                          </button>
                          <button 
                            className="action-btn small danger"
                            onClick={() => handleDeleteAchievement(achievement.id)}
                          >
                            刪除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'statistics' && (
          <div className="statistics-section">
            <h2>統計分析</h2>
            
            <div className="stats-grid">
              <div className="stats-card">
                <h3>整體統計</h3>
                <div className="stats-content">
                  <div className="stat-item">
                    <span className="stat-label">總活動數:</span>
                    <span className="stat-value">{slpData.totalActivities}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">總成就數:</span>
                    <span className="stat-value">{slpData.totalAchievements}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">參與學生:</span>
                    <span className="stat-value">{slpData.uniqueStudents}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">涉及班別:</span>
                    <span className="stat-value">{slpData.uniqueClasses}</span>
                  </div>
                </div>
              </div>

              <div className="stats-card">
                <h3>活動類別統計</h3>
                <div className="category-stats-list">
                  {Object.values(slpData.categoryGroups).map((group, index) => (
                    <div key={index} className="category-stat-item">
                      <div className="category-name">{group.category}</div>
                      <div className="category-stats">
                        <span className="stat">{group.totalActivities} 個活動</span>
                        <span className="stat">{group.totalAchievements} 個成就</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="stats-card">
                <h3>班別統計</h3>
                <div className="class-stats-list">
                  {Object.values(slpData.classGroups).map((group, index) => (
                    <div key={index} className="class-stat-item">
                      <div className="class-name">{group.className}</div>
                      <div className="class-stats">
                        <span className="stat">{group.totalActivities} 個活動</span>
                        <span className="stat">{group.totalAchievements} 個成就</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SLPManagementPage;